import * as Sentry from "@sentry/react";
import { BrowserOptions, SeverityLevel } from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { getCurrentEnv, getSentryDsn, getSentryRelease } from "~/common/utils";
import {
  ApiNetworkError,
  AWSAuthError,
  AWSRequestAbortedError,
  LambdaError,
  NetworkError,
  S3Error,
  UnauthorizedAccessError,
  UserError,
} from "./exceptions";
import { isGraphQLError } from "./graphqlErrors";

export type { SeverityLevel };

export interface ReportOptions {
  level?: SeverityLevel;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  force?: boolean;
}

export interface InitOptions {
  enableReactRouterIntegration?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Breadcrumb categories organized by value tier for environment-based filtering
 */
const BREADCRUMB_CATEGORIES = {
  // High-value categories that should always be included
  HIGH_VALUE: [
    "navigation", // Page navigation
    "feature", // Feature usage
    "api", // API calls
    "error", // Error-related actions
    "user_action", // Important user actions
    "form_submission", // Form submissions
    "file_upload", // File operations
    "authentication", // Auth events
  ],

  // Medium-value categories (include in dev/staging, filter in prod)
  MEDIUM_VALUE: [
    "ui_action", // UI interactions
    "state_change", // State changes
    "data_fetch", // Data fetching
  ],

  // Low-value categories (only in dev)
  LOW_VALUE: [
    "debug", // Debug information
    "performance", // Performance metrics
    "analytics", // Analytics events
  ],
} as const;

/**
 * Fraction of traces sent, replacing the 100% the SDK was first wired up with.
 *
 * Page loads are a small share of Portal span volume; the weight is in the API
 * calls each one fans out into. A quarter keeps enough page loads for stable
 * p75 Web Vitals while bringing the per-page fan-out back down to something
 * proportionate to what anyone reads.
 */
const TRACES_SAMPLE_RATE = 0.25;

/**
 * Static-asset load spans dropped before they are sent.
 *
 * A chunked bundle emits one of these per file on every page load, which made
 * them roughly half of the Portal's span volume, and nothing reads them: this
 * is an authenticated internal tool served from a CDN, so asset timing is not
 * an input to any decision we make. Web Vitals are unaffected — LCP/FCP/CLS
 * are measurements on the pageload span, not resource spans.
 *
 * Ops are matched exactly against `resource.${initiatorType}`. `img` and
 * `image` are both listed because the initiator type varies by browser.
 */
const IGNORED_RESOURCE_SPAN_OPS = [
  "resource.script",
  "resource.link",
  "resource.css",
  "resource.img",
  "resource.image",
  "resource.icon",
  "resource.iframe",
  "resource.other",
];

/**
 * Strict type for breadcrumb categories
 */
export type BreadcrumbCategory =
  | (typeof BREADCRUMB_CATEGORIES.HIGH_VALUE)[number]
  | (typeof BREADCRUMB_CATEGORIES.MEDIUM_VALUE)[number]
  | (typeof BREADCRUMB_CATEGORIES.LOW_VALUE)[number];

/**
 * Helper functions to check category membership with proper typing
 */
const isHighValueCategory = (
  category: BreadcrumbCategory,
): category is (typeof BREADCRUMB_CATEGORIES.HIGH_VALUE)[number] => {
  return BREADCRUMB_CATEGORIES.HIGH_VALUE.includes(
    category as (typeof BREADCRUMB_CATEGORIES.HIGH_VALUE)[number],
  );
};

const isMediumValueCategory = (
  category: BreadcrumbCategory,
): category is (typeof BREADCRUMB_CATEGORIES.MEDIUM_VALUE)[number] => {
  return BREADCRUMB_CATEGORIES.MEDIUM_VALUE.includes(
    category as (typeof BREADCRUMB_CATEGORIES.MEDIUM_VALUE)[number],
  );
};

const isLowValueCategory = (
  category: BreadcrumbCategory,
): category is (typeof BREADCRUMB_CATEGORIES.LOW_VALUE)[number] => {
  return BREADCRUMB_CATEGORIES.LOW_VALUE.includes(
    category as (typeof BREADCRUMB_CATEGORIES.LOW_VALUE)[number],
  );
};

// Removed hashUserId function as user ID hashing is not needed

/**
 * ErrorReporter - A comprehensive error reporting service for the Portal application.
 *
 * This service provides a centralized way to report errors to Sentry with enhanced
 * context and integration with the application's existing error handling infrastructure.
 *
 * ## Features:
 * - Singleton pattern for consistent usage across the application
 * - Environment-aware configuration (dev/stage/prod)
 * - Integration with custom error classes (LambdaError, S3Error, etc.)
 * - React error boundary support
 * - User context tracking with PII safety
 * - Session context tracking for debugging
 * - Smart breadcrumb filtering to reduce noise
 * - Utility methods for common error reporting patterns
 * - Global error handlers for unhandled errors
 *
 * ## Quick Start:
 * ```typescript
 * // Initialize in your app entry point
 * errorReporter.init();
 * errorReporter.setupGlobalHandlers();
 *
 * // Report errors
 * try {
 *   await someRiskyOperation();
 * } catch (error) {
 *   errorReporter.reportApplicationError(error, {
 *     tags: { component: 'UserProfile' },
 *     extra: { userId: '123' }
 *   });
 * }
 * ```
 *
 * ## User Context Setup:
 * ```typescript
 * // When user logs in (use hashed/anonymized ID)
 * const hashedUserId = hashUserId(user.id);
 * errorReporter.setAnonymousUserContext(hashedUserId, user.role);
 *
 * // Set session context
 * const sessionId = `session_${hashedUserId}_${Date.now()}`;
 * errorReporter.setSessionContext(sessionId, 'production');
 *
 * // When user logs out
 * errorReporter.clearUserContext();
 * ```
 *
 * ## Breadcrumb Usage:
 * ```typescript
 * // High-value breadcrumbs (always captured)
 * errorReporter.addNavigationBreadcrumb('/appointments', '/appointments/123');
 * errorReporter.addFeatureBreadcrumb('appointment-creation', 'started');
 * errorReporter.addApiBreadcrumb('/api/appointments', 'POST', 201);
 * errorReporter.addFormSubmissionBreadcrumb('patient-form', true);
 * errorReporter.addUserActionBreadcrumb('export-medical-record');
 *
 * // Medium-value breadcrumbs (dev/staging only)
 * errorReporter.addBreadcrumb('State updated', 'state_change', { field: 'status' });
 *
 * // Low-value breadcrumbs (dev only)
 * errorReporter.addBreadcrumb('Debug info', 'debug', { value: 'test' });
 * ```
 *
 * ## Error Boundary Integration:
 * ```typescript
 * <ErrorBoundary fallback={<ErrorFallback />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * ## PII Safety Guidelines:
 *
 * ⚠️ **NEVER log personally identifiable information (PII)**:
 * - Email addresses, names, phone numbers
 * - Social security numbers, medical record numbers
 * - Any other sensitive personal data
 *
 * ✅ **Safe to log**:
 * - Hashed/anonymized user IDs (use `hashUserId()`)
 * - User roles (doctor, medical_assistant, etc.)
 * - Session IDs, environment information
 * - Component names, error types, stack traces
 * - Non-sensitive metadata
 *
 * ## Environment Behavior:
 * - **Development**: Full breadcrumb capture, debug mode enabled
 * - **Staging**: Medium breadcrumb filtering, debug mode disabled
 * - **Production**: Aggressive breadcrumb filtering, minimal noise
 *
 * ## Breadcrumb Categories:
 *
 * ### High-Value (Always Captured)
 * - `navigation` - Page navigation events
 * - `feature` - Feature usage and actions
 * - `api` - API calls and responses
 * - `error` - Error-related actions
 * - `user_action` - Important user interactions
 * - `form_submission` - Form submissions
 * - `file_upload` - File operations
 * - `authentication` - Auth events
 *
 * ### Medium-Value (Dev/Staging Only)
 * - `ui_action` - UI interactions
 * - `state_change` - State changes
 * - `data_fetch` - Data fetching operations
 *
 * ### Low-Value (Dev Only)
 * - `debug` - Debug information
 * - `performance` - Performance metrics
 * - `analytics` - Analytics events
 */
class ErrorReporter {
  private static instance: ErrorReporter;
  private isInitialized = false;

  private constructor() {
    // Private constructor to prevent direct instantiation.
  }

  // ============================================================================
  // SINGLETON PATTERN
  // ============================================================================

  /**
   * Gets the singleton instance of the ErrorReporter.
   *
   * @returns The singleton instance of the ErrorReporter.
   *
   * @example
   * ```typescript
   * const reporter = ErrorReporter.getInstance();
   * reporter.init();
   * ```
   */
  public static getInstance(): ErrorReporter {
    if (!ErrorReporter.instance) {
      ErrorReporter.instance = new ErrorReporter();
    }
    return ErrorReporter.instance;
  }

  // ============================================================================
  // INITIALIZATION & CONFIGURATION
  // ============================================================================

  /**
   * Initializes the error reporting client with environment-aware configuration.
   *
   * This method should be called once at application startup, typically in your
   * main entry point (e.g., index.tsx). The configuration automatically adjusts
   * based on the current environment to optimize for debugging vs. production.
   *
   * **Configuration by Environment:**
   * - **Development**: Full breadcrumb capture, debug mode enabled
   * - **Staging**: Medium breadcrumb filtering, debug mode disabled
   * - **Production**: Aggressive breadcrumb filtering, minimal noise
   *
   * **React Router Integration:**
   * To enable React Router v6 tracing for better navigation tracking and performance monitoring:
   * ```typescript
   * // In your app entry point (e.g., index.tsx)
   * errorReporter.init({ enableReactRouterIntegration: true });
   * ```
   *
   * @example
   * ```typescript
   * // Basic initialization
   * errorReporter.init();
   * errorReporter.setupGlobalHandlers();
   *
   * // With React Router integration
   * errorReporter.init({ enableReactRouterIntegration: true });
   * errorReporter.setupGlobalHandlers();
   * ```
   *
   * @throws {Error} If Sentry DSN is not configured
   */
  public init(options?: InitOptions): void {
    const currentEnv = getCurrentEnv();
    const dsn = getSentryDsn();
    const release = getSentryRelease();

    if (!dsn) {
      console.warn("Sentry DSN not found, skipping initialization.");
      return;
    }

    if (!release) {
      console.warn("Sentry release unset");
    }

    const integrations: BrowserOptions["integrations"] = [];

    // Add React Router integration if provided
    if (options?.enableReactRouterIntegration) {
      integrations.push(
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
          ignoreResourceSpans: IGNORED_RESOURCE_SPAN_OPS,
        }),
      );
    }

    integrations.push(
      Sentry.graphqlClientIntegration({
        // Match any endpoint containing '/graphql' as a path segment
        endpoints: [/\/graphql$/],
      }),
    );

    Sentry.init({
      dsn,
      environment: currentEnv,
      debug: currentEnv !== "prod",
      integrations,
      enabled: currentEnv !== "dev",
      release: release || "unknown",

      // UX metrics (see src/analytics). Default is already true; set
      // explicitly so a future SDK default flip can't silently stop ingestion.
      enableMetrics: true,

      // Hook to filter or modify events before they are sent to Sentry
      beforeSend(event) {
        const originalMessage = event.exception?.values?.[0]?.value;

        // Downgrade a specific React error in Firefox that is not critical to 'debug' level.
        // This error "Should not already be working." can occur when a window
        // prompt is opened, and we've decided to report it with lower severity.
        if (originalMessage?.includes("Should not already be working.")) {
          event.level = "debug";
        }

        return event;
      },

      // Breadcrumb configuration to reduce noise
      beforeBreadcrumb(breadcrumb) {
        // Filter console breadcrumbs in production, but keep errors
        if (currentEnv === "prod" && breadcrumb.category === "console") {
          const breadcrumbData = breadcrumb.data as
            | Record<string, unknown>
            | undefined;
          const level = breadcrumbData?.level as string | undefined;

          if (level === "error") {
            return breadcrumb;
          }

          // Filter out other console breadcrumbs (log, info, debug, warning)
          return null;
        }

        // Filter out DOM breadcrumbs (clicks, etc.) in production
        if (currentEnv === "prod" && breadcrumb.category === "ui.click") {
          return null;
        }

        // Filter out fetch breadcrumbs (we'll add our own API breadcrumbs)
        // But allow POST requests with error statuses for debugging
        if (currentEnv === "prod" && breadcrumb.category === "fetch") {
          // Check if this is a POST request with an error status
          const breadcrumbData = breadcrumb.data as
            | Record<string, unknown>
            | undefined;
          const method = breadcrumbData?.method as string | undefined;
          const statusCode = breadcrumbData?.status_code as number | undefined;

          // Allow POST requests with error statuses (4xx, 5xx)
          if (method === "POST" && statusCode && statusCode >= 400) {
            return breadcrumb;
          }

          // Filter out all other fetch breadcrumbs
          return null;
        }

        return breadcrumb;
      },

      tracesSampleRate: TRACES_SAMPLE_RATE,
    });

    this.isInitialized = true;
  }

  /**
   * Sets up global error handlers for unhandled errors.
   *
   * This method registers global event listeners to catch unhandled JavaScript errors
   * and unhandled promise rejections. It should be called once at application startup
   * after the ErrorReporter is initialized.
   *
   * **Handled Events:**
   * - `unhandledrejection`: Catches unhandled promise rejections
   * - `error`: Catches uncaught JavaScript errors
   *
   * **Note**: This method must be called after `init()` has been called.
   *
   * @example
   * ```typescript
   * // In your app entry point (e.g., index.tsx)
   * errorReporter.init();
   * errorReporter.setupGlobalHandlers();
   *
   * // Now unhandled errors will be automatically captured
   * // Example of what gets caught:
   * // - Unhandled promise rejections
   * // - JavaScript runtime errors
   * // - Network errors that bubble up
   * ```
   */
  public setupGlobalHandlers(): void {
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      return;
    }

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      this.report(
        new Error(`Unhandled Promise Rejection: ${String(event.reason)}`),
        {
          tags: { errorType: "unhandled_promise_rejection" },
          extra: { reason: event.reason },
        },
      );
    });

    // Handle uncaught errors
    window.addEventListener("error", (event) => {
      this.report(new Error(`Uncaught Error: ${event.message}`), {
        tags: { errorType: "uncaught_error" },
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });
  }

  // ============================================================================
  // CORE ERROR REPORTING
  // ============================================================================

  /**
   * Reports an error to Sentry with optional context and metadata.
   *
   * This is the basic error reporting method. For application-specific errors,
   * consider using `reportApplicationError()` which provides enhanced context
   * based on error type.
   *
   * @param error - The error object to report
   * @param options - Optional configuration for the error report
   * @param options.level - The severity level (default: "error")
   * @param options.tags - Key-value pairs for categorizing and filtering errors
   * @param options.extra - Additional context data for debugging
   *
   * @example
   * ```typescript
   * try {
   *   await riskyOperation();
   * } catch (error) {
   *   errorReporter.report(error, {
   *     level: 'error',
   *     tags: {
   *       component: 'UserProfile',
   *       operation: 'data-fetch'
   *     },
   *     extra: {
   *       userId: hashUserId(user.id),
   *       timestamp: new Date().toISOString()
   *     }
   *   });
   * }
   * ```
   */
  public report(error: Error, options?: ReportOptions): void {
    const reportableError = error as Error & { _isReported?: boolean };

    // Avoid reporting the same error instance multiple times, unless forced
    if (reportableError._isReported && !options?.force) {
      return;
    }

    if (getCurrentEnv() === "dev") {
      // in dev, we don't want to report errors to Sentry, just log them to the console
      console.error(error);
      return;
    }
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      console.error("report: ", error);
      return;
    }

    const eventId = Sentry.withScope((scope) => {
      scope.setLevel(options?.level ?? "error");
      if (options?.tags) {
        scope.setTags(options.tags);
      }
      if (options?.extra) {
        scope.setExtras(options.extra);
      }
      return Sentry.captureException(error);
    });

    // Set a flag on the error instance to indicate it has been reported,
    // only if Sentry accepted the event (which returns an event ID).
    if (eventId) {
      reportableError._isReported = true;
    }
  }

  /**
   * Reports a message to Sentry, with optional extra context.
   * @param message The message to report.
   * @param options An object containing extra context to be sent with the message.
   */
  public reportMessage(message: string, options?: ReportOptions): void {
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      return;
    }
    Sentry.withScope((scope) => {
      scope.setLevel(options?.level ?? "info");
      if (options?.tags) {
        scope.setTags(options.tags);
      }
      if (options?.extra) {
        scope.setExtras(options.extra);
      }
      Sentry.captureMessage(message);
    });
  }

  /**
   * Reports application-specific errors with enhanced context based on error type.
   *
   * This method automatically detects the error type and adds appropriate context,
   * tags, and severity levels. It's the recommended method for reporting
   * application errors as it provides better debugging information.
   *
   * **Automatic Error Type Detection:**
   * - `LambdaError`: Adds service="lambda" tag and function name extraction
   * - `S3Error`: Adds service="s3" tag for storage-related errors
   * - `NetworkError`: Adds service="network" tag for connectivity issues
   * - `AWSAuthError`: Adds service="auth" tag for authentication errors
   * - `UserError`: Sets level to "warning" and adds category="user_error" tag
   * - `GraphQLError`: Adds service="graphql" tag and GraphQL error details
   * - Other errors: Adds basic error type and stack trace information
   *
   * @param error - The application error to report
   * @param options - Additional reporting options (merged with auto-detected options)
   *
   * @example
   * ```typescript
   * // Lambda function error
   * try {
   *   await lambda.invoke({ FunctionName: 'processData' });
   * } catch (error) {
   *   errorReporter.reportApplicationError(error, {
   *     tags: { component: 'DataProcessor' },
   *     extra: { dataId: '123' }
   *   });
   *   // Automatically adds: service="lambda", functionName="processData"
   * }
   *
   * // User input validation error
   * try {
   *   validateUserInput(data);
   * } catch (error) {
   *   if (error instanceof UserError) {
   *     errorReporter.reportApplicationError(error);
   *     // Automatically sets level="warning", category="user_error"
   *   }
   * }
   *
   * // Network error
   * try {
   *   await fetch('/api/data');
   * } catch (error) {
   *   errorReporter.reportApplicationError(error, {
   *     tags: { endpoint: '/api/data' }
   *   });
   *   // Automatically adds: service="network"
   * }
   *
   * // GraphQL error (using toError utility)
   * try {
   *   const response = await amplifyClient.graphql({ query, variables });
   *   if (response.errors) {
   *     throw response; // This will be handled by toError()
   *   }
   * } catch (error) {
   *   const errorObj = toError(error, "GraphQL operation failed");
   *   errorReporter.reportApplicationError(errorObj);
   *   // Automatically adds: service="graphql", graphQLErrors, originalResponse
   * }
   * ```
   */
  public reportApplicationError(error: Error, options?: ReportOptions): void {
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      console.error("reportApplicationError: ", error);
      return;
    }

    const enhancedOptions: ReportOptions = {
      ...options,
      tags: {
        ...options?.tags,
        errorType: error.constructor.name,
      },
      extra: {
        ...options?.extra,
        errorName: error.name,
        errorStack: error.stack,
      },
    };

    // Add specific context based on error type.
    // ApiNetworkError must be checked before NetworkError (its superclass).
    if (error instanceof ApiNetworkError) {
      // Transient browser-level fetch reject (no HTTP response). Default to
      // warning so it doesn't trip error-level alerts; a caller may still
      // override by passing an explicit `level`.
      enhancedOptions.level = enhancedOptions.level ?? "warning";
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "network",
        networkLayer: "fetch",
        aborted: String(error.aborted),
        endpoint: error.endpoint,
      };
    } else if (error instanceof AWSRequestAbortedError) {
      // Browser-cancelled AWS request (navigation/unmount/tab close). Same
      // non-actionable class as ApiNetworkError above, so default to warning;
      // a caller may still override by passing an explicit `level`.
      enhancedOptions.level = enhancedOptions.level ?? "warning";
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "lambda",
        aborted: "true",
        functionName: error.functionName,
      };
      enhancedOptions.extra = {
        ...enhancedOptions.extra,
        originalError: error.originalError,
      };
    } else if (error instanceof LambdaError) {
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "lambda",
        functionName: error.message.match(/\(([^)]+)\)/)?.[1] || "unknown",
      };
      // Add original error to extra
      enhancedOptions.extra = {
        ...enhancedOptions.extra,
        ...(error.extra ?? {}),
        originalError: error.originalError,
      };
    } else if (error instanceof S3Error) {
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "s3",
      };
      // Add S3-specific details and original error to extra
      enhancedOptions.extra = {
        ...enhancedOptions.extra,
        s3Operation: error.operation,
        s3Bucket: error.bucket,
        s3Key: error.key,
        originalError: error.originalError,
      };
    } else if (error instanceof NetworkError) {
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "network",
      };
    } else if (error instanceof AWSAuthError) {
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "auth",
      };
      // Add original error to extra
      enhancedOptions.extra = {
        ...enhancedOptions.extra,
        originalError: error.originalError,
      };
    } else if (error instanceof UnauthorizedAccessError) {
      enhancedOptions.level = "warning";
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "authorization",
        resourceType: error.resourceType,
      };
      enhancedOptions.extra = {
        ...enhancedOptions.extra,
        resourceId: error.resourceId,
      };
    } else if (error instanceof UserError) {
      enhancedOptions.level = "warning";
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        category: "user_error",
      };
    } else if (isGraphQLError(error)) {
      // Handle GraphQL errors created by graphql() wrapper or toError()
      enhancedOptions.tags = {
        ...enhancedOptions.tags,
        service: "graphql",
      };
      // Add GraphQL-specific metadata to extra
      enhancedOptions.extra = {
        ...enhancedOptions.extra,
        graphQLErrors: error.graphQLErrors,
        partialErrors: error.partialErrors,
        originalResponse: error.originalResponse,
      };
    }

    this.report(error, enhancedOptions);
  }

  /**
   * Reports React errors for error boundaries with React-specific context.
   *
   * This method is used internally by the ErrorBoundary component to capture
   * React errors and report them to Sentry with additional React-specific context.
   * It automatically adds React-specific tags and extras while allowing custom
   * options to be merged in.
   *
   * @param error - The React error that occurred
   * @param errorInfo - Additional error information from React (component stack, etc.)
   * @param options - Optional reporting options (merged with React-specific defaults)
   *
   * @example
   * ```typescript
   * // This is typically called by the ErrorBoundary component
   * componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
   *   errorReporter.reportReactError(error, errorInfo);
   * }
   *
   * // With custom options
   * componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
   *   errorReporter.reportReactError(error, errorInfo, {
   *     tags: { component: 'UserProfile' },
   *     extra: { userId: hashUserId(user.id) }
   *   });
   * }
   * ```
   */
  public reportReactError(
    error: Error,
    errorInfo: React.ErrorInfo,
    options?: ReportOptions,
  ): void {
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      console.error("reportReactError: ", error);
      return;
    }

    const reactOptions: ReportOptions = {
      level: options?.level ?? "error",
      tags: {
        errorType: "react_error",
        ...options?.tags,
      },
      extra: {
        componentStack: errorInfo.componentStack,
        ...options?.extra,
      },
      force: options?.force ?? false,
    };

    this.report(error, reactOptions);
  }

  // ============================================================================
  // USER & SESSION CONTEXT
  // ============================================================================

  /**
   * Sets anonymous user context for error reporting (no PII).
   *
   * This method sets user context that will be included with all error reports.
   * The user ID should be hashed/anonymized to prevent PII exposure. User context
   * helps correlate errors with specific users and roles for debugging.
   *
   * This method sets user context that will be included with all error reports.
   *
   * @param userId - User identifier
   * @param userRole - The user's role/group (e.g., 'Doctor', 'MedicalAssistant', 'Admin')
   *
   * @example
   * ```typescript
   * // When user logs in
   * const handleLogin = (user) => {
   *   errorReporter.setAnonymousUserContext(user.id, user.role);
   * };
   *
   * // When user role changes
   * const handleRoleChange = (newRole) => {
   *   errorReporter.setAnonymousUserContext(currentUser.id, newRole);
   * };
   * ```
   */
  public setAnonymousUserContext(userId: string, userRole?: string): void {
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      return;
    }
    Sentry.setUser({
      id: userId,
      userGroup: userRole,
    });
  }

  /**
   * Clears user context from error reporting.
   *
   * This method removes user context from all future error reports. It should be
   * called when a user logs out or when you want to stop associating errors with
   * a specific user.
   *
   * @example
   * ```typescript
   * // When user logs out
   * const handleLogout = () => {
   *   errorReporter.clearUserContext();
   *   // ... logout logic
   * };
   *
   * // When switching to anonymous mode
   * const switchToAnonymous = () => {
   *   errorReporter.clearUserContext();
   *   errorReporter.setSessionContext('anonymous', getCurrentEnv());
   * };
   * ```
   */
  public clearUserContext(): void {
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      return;
    }
    Sentry.setUser(null);
  }

  /**
   * Sets session context for error reporting (no PII).
   *
   * This method sets session context that will be included with all error reports.
   * Session context helps correlate errors with specific user sessions for debugging
   * and can help identify patterns across user sessions.
   *
   * @param sessionId - A unique session identifier (e.g., 'session_user_abc123_1703123456789')
   * @param environment - The current environment (e.g., 'production', 'staging', 'development')
   *
   * @example
   * ```typescript
   * // When user session starts
   * const startSession = (userId: string) => {
   *   const sessionId = `session_${userId}_${Date.now()}`;
   *   errorReporter.setSessionContext(sessionId, getCurrentEnv());
   * };
   *
   * // For anonymous sessions
   * const startAnonymousSession = () => {
   *   const sessionId = `anonymous_${Date.now()}`;
   *   errorReporter.setSessionContext(sessionId, getCurrentEnv());
   * };
   * ```
   */
  public setSessionContext(sessionId: string, environment?: string): void {
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      return;
    }
    Sentry.setTag("session_id", sessionId);
    if (environment) {
      Sentry.setTag("environment", environment);
    }
  }

  // ============================================================================
  // BREADCRUMB MANAGEMENT
  // ============================================================================

  /**
   * Adds breadcrumb for debugging and tracing.
   * @param message The breadcrumb message.
   * @param category The breadcrumb category.
   * @param data Additional data for the breadcrumb.
   */
  public addBreadcrumb(
    message: string,
    category: BreadcrumbCategory,
    data?: Record<string, unknown>,
  ): void {
    if (getCurrentEnv() === "dev") {
      // in dev, we don't want to report errors to Sentry, just log them to the console
      console.debug("Breadcrumb:", message, category, data);
      return;
    }
    if (!this.isInitialized) {
      console.warn(
        "ErrorReporter not initialized. Call errorReporter.init() first.",
      );
      return;
    }

    // Filter out noisy breadcrumbs in production
    const currentEnv = getCurrentEnv();
    const shouldInclude = this.shouldIncludeBreadcrumb(category, currentEnv);

    if (!shouldInclude) {
      return;
    }

    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: "info",
    });
  }

  /**
   * Determines whether a breadcrumb should be included based on category and environment.
   * @param category The breadcrumb category.
   * @param environment The current environment.
   * @returns Whether the breadcrumb should be included.
   */
  private shouldIncludeBreadcrumb(
    category: BreadcrumbCategory,
    environment: string,
  ): boolean {
    // Always include high-value categories
    if (isHighValueCategory(category)) {
      return true;
    }

    // Include medium-value categories in dev and staging
    if (isMediumValueCategory(category) && environment !== "prod") {
      return true;
    }

    // Only include low-value categories in dev
    if (isLowValueCategory(category) && environment === "dev") {
      return true;
    }

    return false;
  }

  // ============================================================================
  // BREADCRUMB UTILITY METHODS
  // ============================================================================

  /**
   * Adds a navigation breadcrumb for page changes and routing events.
   *
   * Navigation breadcrumbs are high-value and always captured. They help
   * debug issues related to routing, page transitions, and user navigation flow.
   *
   * @param fromPage - The page the user is coming from (e.g., '/appointments')
   * @param toPage - The page the user is going to (e.g., '/appointments/123')
   * @param userId - Optional hashed user ID for context (use hashUserId())
   *
   * @example
   * ```typescript
   * // In your router or navigation components
   * const handleNavigation = (toPage: string) => {
   *   errorReporter.addNavigationBreadcrumb(currentPage, toPage, hashedUserId);
   *   navigate(toPage);
   * };
   *
   * // In React Router
   * useEffect(() => {
   *   errorReporter.addNavigationBreadcrumb(previousPath, pathname, hashedUserId);
   * }, [pathname]);
   * ```
   */
  public addNavigationBreadcrumb(
    fromPage: string,
    toPage: string,
    userId?: string,
  ): void {
    this.addBreadcrumb(`Navigation: ${fromPage} → ${toPage}`, "navigation", {
      fromPage,
      toPage,
      userId,
    });
  }

  /**
   * Adds a feature usage breadcrumb for important user actions and feature interactions.
   *
   * Feature breadcrumbs are high-value and always captured. Use these for tracking
   * important user actions that could be related to errors or debugging user workflows.
   *
   * @param feature - The feature being used (e.g., 'appointment-creation', 'medical-record-export')
   * @param action - The specific action performed (e.g., 'started', 'completed', 'failed')
   * @param data - Additional context data (avoid PII)
   *
   * @example
   * ```typescript
   * // When user starts creating an appointment
   * const handleCreateAppointment = () => {
   *   errorReporter.addFeatureBreadcrumb('appointment-creation', 'started', {
   *     appointmentType: 'follow-up',
   *     hasExistingPatient: true
   *   });
   *   // ... appointment creation logic
   * };
   *
   * // When feature completes successfully
   * const handleExportComplete = () => {
   *   errorReporter.addFeatureBreadcrumb('medical-record-export', 'completed', {
   *     recordCount: 5,
   *     format: 'pdf'
   *   });
   * };
   * ```
   */
  public addFeatureBreadcrumb(
    feature: string,
    action: string,
    data?: Record<string, unknown>,
  ): void {
    this.addBreadcrumb(`${feature}: ${action}`, "feature", {
      feature,
      action,
      ...data,
    });
  }

  /**
   * Adds an API call breadcrumb for debugging API issues and network problems.
   *
   * API breadcrumbs are high-value and always captured. They help debug issues
   * related to API calls, network connectivity, and server responses.
   *
   * @param endpoint - The API endpoint being called (e.g., '/api/appointments')
   * @param method - The HTTP method (e.g., 'GET', 'POST', 'PUT', 'DELETE')
   * @param statusCode - The response status code (optional, for completed requests)
   *
   * @example
   * ```typescript
   * // In your API service layer
   * const fetchAppointments = async () => {
   *   try {
   *     errorReporter.addApiBreadcrumb('/api/appointments', 'GET');
   *     const response = await fetch('/api/appointments');
   *     errorReporter.addApiBreadcrumb('/api/appointments', 'GET', response.status);
   *     return response.json();
   *   } catch (error) {
   *     errorReporter.addApiBreadcrumb('/api/appointments', 'GET', 0); // 0 for network errors
   *     throw error;
   *   }
   * };
   *
   * // In your GraphQL client
   * const createAppointment = async (data) => {
   *   errorReporter.addApiBreadcrumb('/graphql', 'POST');
   *   const result = await client.mutate({ mutation: CREATE_APPOINTMENT, variables: data });
   *   errorReporter.addApiBreadcrumb('/graphql', 'POST', 200);
   *   return result;
   * };
   * ```
   */
  public addApiBreadcrumb(
    endpoint: string,
    method: string,
    statusCode?: number,
  ): void {
    this.addBreadcrumb(
      `API ${method} ${endpoint}${statusCode ? ` (${statusCode})` : ""}`,
      "api",
      { endpoint, method, statusCode },
    );
  }

  /**
   * Adds a form submission breadcrumb for debugging form issues and user input problems.
   *
   * Form submission breadcrumbs are high-value and always captured. They help debug
   * issues related to form validation, submission failures, and user input errors.
   *
   * @param formName - The name of the form (e.g., 'patient-form', 'appointment-form')
   * @param success - Whether the submission was successful
   * @param errorMessage - Optional error message for failed submissions
   *
   * @example
   * ```typescript
   * // In your form submission handler
   * const handleSubmit = async (formData) => {
   *   try {
   *     const response = await submitForm(formData);
   *     errorReporter.addFormSubmissionBreadcrumb('patient-form', true);
   *     // Handle success
   *   } catch (error) {
   *     errorReporter.addFormSubmissionBreadcrumb('patient-form', false, error.message);
   *     // Handle error
   *   }
   * };
   *
   * // In your form validation
   * const validateForm = (data) => {
   *   const errors = validate(data);
   *   if (errors.length > 0) {
   *     errorReporter.addFormSubmissionBreadcrumb('appointment-form', false,
   *       `Validation failed: ${errors.join(', ')}`);
   *   }
   *   return errors;
   * };
   * ```
   */
  public addFormSubmissionBreadcrumb(
    formName: string,
    success: boolean,
    errorMessage?: string,
  ): void {
    this.addBreadcrumb(
      `Form submission: ${formName} (${success ? "success" : "failed"})`,
      "form_submission",
      { formName, success, errorMessage },
    );
  }

  /**
   * Adds a user action breadcrumb for important user interactions and decisions.
   *
   * User action breadcrumbs are high-value and always captured. Use these for tracking
   * important user decisions, interactions, or actions that could be relevant to errors.
   *
   * @param action - The action performed (e.g., 'export-medical-record', 'delete-appointment')
   * @param context - Additional context about the action (avoid PII)
   *
   * @example
   * ```typescript
   * // When user exports medical records
   * const handleExport = (recordId: string) => {
   *   errorReporter.addUserActionBreadcrumb('export-medical-record', {
   *     recordId,
   *     format: 'pdf',
   *     includeAttachments: true
   *   });
   *   // ... export logic
   * };
   *
   * // When user deletes an appointment
   * const handleDelete = (appointmentId: string) => {
   *   errorReporter.addUserActionBreadcrumb('delete-appointment', {
   *     appointmentId,
   *     reason: 'cancelled',
   *     hasNotes: true
   *   });
   *   // ... deletion logic
   * };
   *
   * // When user changes settings
   * const handleSettingChange = (setting: string, value: any) => {
   *   errorReporter.addUserActionBreadcrumb('change-setting', {
   *     setting,
   *     previousValue: currentValue,
   *     newValue: value
   *   });
   *   // ... setting update logic
   * };
   * ```
   */
  public addUserActionBreadcrumb(
    action: string,
    context?: Record<string, unknown>,
  ): void {
    this.addBreadcrumb(`User action: ${action}`, "user_action", {
      action,
      ...context,
    });
  }
}

/**
 * The singleton instance of the ErrorReporter.
 */
export const errorReporter = ErrorReporter.getInstance();
